import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import VM from 'scratch-vm';

import {
    createOperationError,
    createExportResult,
    createLoadResult,
    isValidProjectPayload,
    validateExportRequest,
    validateLoadRequest,
    validateLoadRequestMetadata
} from './future-innovator-project-protocol.js';

const SCRATCH_SB3_BLOB_MIME_TYPE = 'application/x.scratch.sb3';

const isScratchSb3Blob = value => (
    typeof Blob !== 'undefined' &&
    value instanceof Blob &&
    value.type === SCRATCH_SB3_BLOB_MIME_TYPE &&
    typeof value.arrayBuffer === 'function'
);

const FutureInnovatorProjectBridgeHOC = function (WrappedComponent) {
    class FutureInnovatorProjectBridge extends React.Component {
        constructor (props) {
            super(props);
            this.pendingRequestIds = new Set();
            bindAll(this, [
                'handleMessage',
                'postError',
                'postToParent'
            ]);
        }
        componentDidMount () {
            window.addEventListener('message', this.handleMessage);
        }
        componentWillUnmount () {
            window.removeEventListener('message', this.handleMessage);
            this.pendingRequestIds.clear();
        }
        postToParent (message, transfer) {
            if (window.parent === window) return;
            if (transfer) {
                window.parent.postMessage(message, window.location.origin, transfer);
            } else {
                window.parent.postMessage(message, window.location.origin);
            }
        }
        postError (request, code) {
            const result = createOperationError(request, code);
            if (result) this.postToParent(result);
        }
        async handleMessage (event) {
            if (window.parent === window || event.origin !== window.location.origin ||
                event.source !== window.parent) {
                return;
            }

            const request = event.data;
            const isExportRequest = validateExportRequest(request);
            const hasValidLoadMetadata = validateLoadRequestMetadata(request);
            if (!isExportRequest && !hasValidLoadMetadata) {
                return;
            }

            const isLoadRequest = hasValidLoadMetadata && validateLoadRequest(request);
            if (hasValidLoadMetadata && !isLoadRequest) {
                this.postError(request, 'PAYLOAD_REJECTED');
                return;
            }

            if (this.pendingRequestIds.has(request.requestId)) {
                this.postError(request, isExportRequest ? 'EXPORT_FAILED' : 'LOAD_FAILED');
                return;
            }
            this.pendingRequestIds.add(request.requestId);

            try {
                if (isExportRequest) {
                    const exportedProject = await this.props.vm.saveProjectSb3();
                    if (!isScratchSb3Blob(exportedProject)) {
                        this.postError(request, 'PAYLOAD_REJECTED');
                        return;
                    }
                    const payload = await exportedProject.arrayBuffer();
                    if (!isValidProjectPayload(payload)) {
                        this.postError(request, 'PAYLOAD_REJECTED');
                        return;
                    }
                    const result = createExportResult(request, payload);
                    if (!result) {
                        this.postError(request, 'PAYLOAD_REJECTED');
                        return;
                    }
                    this.postToParent(result, [payload]);
                    return;
                }

                await this.props.vm.loadProject(request.payload);
                const result = createLoadResult(request);
                if (result) this.postToParent(result);
            } catch (error) {
                this.postError(request, isExportRequest ? 'EXPORT_FAILED' : 'LOAD_FAILED');
            } finally {
                this.pendingRequestIds.delete(request.requestId);
            }
        }
        render () {
            return <WrappedComponent {...this.props} />;
        }
    }

    FutureInnovatorProjectBridge.propTypes = {
        vm: PropTypes.instanceOf(VM).isRequired
    };

    const mapStateToProps = state => ({
        vm: state.scratchGui.vm
    });
    const mergeProps = (stateProps, dispatchProps, ownProps) => Object.assign(
        {}, stateProps, dispatchProps, ownProps
    );

    return connect(mapStateToProps, null, mergeProps)(FutureInnovatorProjectBridge);
};

export {
    FutureInnovatorProjectBridgeHOC as default
};
